import * as THREE from 'three';
import type { EnvironmentDef } from '../config/environments';
import { SIM } from '../config/tuning';
import { CHUNK_LENGTH, SAMPLE_STEP } from '../sim/terrain/chunks';

const WALL_DEPTH = 30; // how far the front face extends below the surface
const BACK_Z = -7; // top surface extends behind the drive line
const LIP_HEIGHT = 0.06;
const GRASS_DEPTH = 0.35; // grass band at the top of the front face
/** The dirt track the car drives on, across Z (the car sits at z = 0, ±0.6 wide). */
const TRACK_FRONT_Z = 0.95;
const TRACK_BACK_Z = -0.95;
/** World metres per texture tile. */
const TOP_TILE = 4;
const FACE_TILE = 6;

const IMAGES = `${import.meta.env.BASE_URL}assets/images/`;

/** One rail of vertices along x: position, UV and a colour tint per column. */
type Rail = (i: number, x: number, h: number) => { y: number; z: number; u: number; v: number; tint: number };

/**
 * Textured terrain, one mesh per physics chunk. Top surface: dirt track between grass verges.
 * Front face: a grass lip over a soil bank whose strata follow the surface. UVs are world-based, so
 * textures continue seamlessly across chunk seams. Three material groups → three draws per chunk.
 */
export class TerrainView {
  readonly group = new THREE.Group();
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly materials: THREE.MeshStandardMaterial[];

  static async create(env: EnvironmentDef): Promise<TerrainView> {
    const loader = new THREE.TextureLoader();
    const t = env.visual.textures;
    const [grass, dirt, soil] = await Promise.all([t.verge, t.track, t.bank].map((f) => loader.loadAsync(IMAGES + f)));
    return new TerrainView(grass, dirt, soil);
  }

  private constructor(grass: THREE.Texture, dirt: THREE.Texture, soil: THREE.Texture) {
    for (const t of [grass, dirt, soil]) {
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 8;
    }
    // Material roles: [0] grass (verges, lip, back field), [1] dirt track, [2] soil bank.
    this.materials = [
      new THREE.MeshStandardMaterial({ map: grass, vertexColors: true, roughness: 0.95 }),
      new THREE.MeshStandardMaterial({ map: dirt, vertexColors: true, roughness: 1 }),
      new THREE.MeshStandardMaterial({ map: soil, vertexColors: true, roughness: 1 }),
    ];
  }

  add(index: number, heights: Float32Array): void {
    this.remove(index);
    const mesh = new THREE.Mesh(this.buildGeometry(index, heights), this.materials);
    mesh.receiveShadow = true;
    this.meshes.set(index, mesh);
    this.group.add(mesh);
  }

  remove(index: number): void {
    const mesh = this.meshes.get(index);
    if (!mesh) return;
    this.group.remove(mesh);
    mesh.geometry.dispose();
    this.meshes.delete(index);
  }

  clear(): void {
    for (const index of [...this.meshes.keys()]) this.remove(index);
  }

  dispose(): void {
    this.clear();
    for (const m of this.materials) {
      m.map?.dispose();
      m.dispose();
    }
    this.group.removeFromParent();
  }

  private buildGeometry(index: number, heights: Float32Array): THREE.BufferGeometry {
    const n = heights.length;
    const x0 = index * CHUNK_LENGTH;
    const front = SIM.terrainHalfDepth;
    const positions: number[] = [];
    const uvs: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const geometry = new THREE.BufferGeometry();

    // Adds a strip of quads between two rails as one material group.
    const strip = (a: Rail, b: Rail, material: number) => {
      const start = indices.length;
      const base = positions.length / 3;
      for (let i = 0; i < n; i++) {
        const x = x0 + i * SAMPLE_STEP;
        for (const rail of [a, b]) {
          const p = rail(i, x, heights[i]);
          positions.push(x, p.y, p.z);
          uvs.push(p.u, p.v);
          colors.push(p.tint, p.tint, p.tint);
        }
      }
      for (let i = 0; i < n - 1; i++) {
        const v = base + i * 2;
        indices.push(v, v + 1, v + 2, v + 2, v + 1, v + 3);
      }
      geometry.addGroup(start, indices.length - start, material);
    };

    const top = (z: number, tint = 1): Rail => (_i, x, h) => ({ y: h, z, u: x / TOP_TILE, v: z / TOP_TILE, tint });
    const face = (dy: number, tint: number, tile = FACE_TILE): Rail => (_i, x, h) => ({ y: h - dy, z: front, u: x / tile, v: dy / tile, tint });

    // Top surface, back to front (each strip faces +Y): back field, dirt track, front verge.
    strip(top(BACK_Z, 0.85), top(TRACK_BACK_Z), 0);
    strip(top(TRACK_BACK_Z), top(TRACK_FRONT_Z), 1);
    strip(top(TRACK_FRONT_Z), (_i, x, h) => ({ y: h + LIP_HEIGHT, z: front, u: x / TOP_TILE, v: front / TOP_TILE, tint: 1.05 }), 0);
    // Front face, top to bottom (facing +Z): grass lip, then the soil bank darkening with depth.
    strip((_i, x, h) => ({ y: h + LIP_HEIGHT, z: front, u: x / 2, v: 0, tint: 1.05 }), face(GRASS_DEPTH, 0.8, 2), 0);
    strip(face(GRASS_DEPTH, 1), face(4, 0.75), 2);
    strip(face(4, 0.75), face(WALL_DEPTH, 0.3), 2);

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }
}
